import React from 'react';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { useRovoEntitlement } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';

import { updateEnabled } from '../../../pm-plugins/rovo-agents/commands';
import type { EditorPluginAIProvider } from '../../../types/types';

export const useRovoEnabled = ({
	editorPluginAIProvider,
}: {
	editorPluginAIProvider: EditorPluginAIProvider;
}): boolean => {
	const { isRovoEnabled } = useRovoEntitlement();
	return (
		!!editorPluginAIProvider.isRovoEnabled &&
		isRovoEnabled &&
		typeof editorPluginAIProvider?.getChannelVariables === 'function'
	);
};

export const useRovoEnabledForCommandPalette = ({
	editorPluginAIProvider,
}: {
	editorPluginAIProvider: EditorPluginAIProvider;
}): boolean => {
	const isRovoEnabled = useRovoEnabled({ editorPluginAIProvider });
	return isRovoEnabled && !!editorPluginAIProvider.showRovoAgentsInCommandPalette;
};

interface RovoEnabledProps {
	children: React.ReactNode;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView;
}

/**
 * This component both:
 * - checks whether Rovo is enabled (flag + entitlement checks) before rendering its children
 * - syncs rovo plugin state with the current evaluated 'enabled' state
 */
export const RovoEnabled = (props: RovoEnabledProps): React.JSX.Element | null => {
	const { editorView, children } = props;
	const enabled = useRovoEnabled({ editorPluginAIProvider: props.editorPluginAIProvider });

	React.useEffect(() => {
		updateEnabled(enabled)(editorView.state, editorView.dispatch);
	}, [editorView.dispatch, editorView.state, enabled]);

	return enabled ? <>{children}</> : null;
};
